import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'motion/react'
import toast from 'react-hot-toast'
import { ChevronDown, Copy, ShieldCheck } from 'lucide-react'
import { hapticImpact, hapticNotify } from '../../../../utils/haptic'
import { CARD_CLASS } from './shared'

// Пример пересчёта из документации эвента: ключ банды по зерну и числу билетов
const VERIFY_SNIPPET = `const crypto = require('crypto');
function key(seed, teamId, tickets) {
  const h = crypto.createHmac('sha256', seed).update(String(teamId)).digest('hex');
  const u = (parseInt(h.slice(0, 8), 16) * 4294967296 + parseInt(h.slice(8, 16), 16) + 1) / 18446744073709551616;
  return Math.log(u) / tickets;
}`

const CopyValue = ({ label, value }) => {
  const { t } = useTranslation()
  const handleCopy = async () => {
    hapticImpact('light')
    try {
      await navigator.clipboard.writeText(value)
      hapticNotify('success')
      toast.success(t('halloweenEvent.fair.copied'))
    } catch {
      hapticNotify('error')
      toast.error(t('halloweenEvent.fair.copyError'))
    }
  }
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wide text-text-secondary">{label}</p>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={t('halloweenEvent.fair.copy')}
          className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-text-secondary transition-all hover:text-white active:scale-[0.95]"
        >
          <Copy size={13} />
        </button>
      </div>
      <p className="mt-1.5 break-all font-mono text-xs text-white/90" dir="ltr">{value}</p>
    </div>
  )
}

/**
 * Проверка честности розыгрыша: хеш зерна опубликован с начала эвента,
 * зерно и таблица проверки — после розыгрыша. Блок свёрнут за кнопкой.
 */
const FairnessCard = ({ draw }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  if (!draw?.seed_hash) return null
  const verify = draw.verify || []

  return (
    <section className={`${CARD_CLASS} overflow-hidden`}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 p-5 text-left transition-colors hover:bg-white/[0.02]"
      >
        <ShieldCheck size={20} className="shrink-0 text-info" />
        <span className="flex-1 text-sm font-bold text-white">{t('halloweenEvent.fair.title')}</span>
        <ChevronDown size={18} className={`shrink-0 text-text-secondary transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="space-y-3 px-5 pb-5">
              <p className="text-xs leading-relaxed text-text-secondary">{t('halloweenEvent.fair.intro')}</p>
              <CopyValue label={t('halloweenEvent.fair.hash')} value={draw.seed_hash} />
              {draw.seed ? (
                <CopyValue label={t('halloweenEvent.fair.seed')} value={draw.seed} />
              ) : (
                <div className="rounded-xl border border-dashed border-white/10 px-3 py-2.5 text-xs text-text-secondary">
                  {t('halloweenEvent.fair.seedHidden')}
                </div>
              )}

              <ol className="list-decimal space-y-1.5 ps-5 text-xs leading-relaxed text-white/85">
                <li>{t('halloweenEvent.fair.step1')}</li>
                <li>{t('halloweenEvent.fair.step2')}</li>
                <li>{t('halloweenEvent.fair.step3')}</li>
              </ol>

              <pre className="overflow-x-auto rounded-xl border border-white/10 bg-black/50 p-3 text-[11px] leading-relaxed text-lime-200" dir="ltr">
                {VERIFY_SNIPPET}
              </pre>

              {verify.length > 0 && (
                <div>
                  <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-text-secondary">
                    {t('halloweenEvent.fair.table')}
                  </p>
                  <div className="max-h-80 overflow-auto rounded-xl border border-white/10">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-surface text-text-secondary">
                        <tr>
                          <th className="px-2 py-2 text-start font-semibold">#</th>
                          <th className="px-2 py-2 text-start font-semibold">{t('halloweenEvent.fair.tableTeam')}</th>
                          <th className="px-2 py-2 text-end font-semibold">team_id</th>
                          <th className="px-2 py-2 text-end font-semibold">{t('halloweenEvent.fair.tableTickets')}</th>
                          <th className="px-2 py-2 text-end font-semibold">key</th>
                        </tr>
                      </thead>
                      <tbody>
                        {verify.map((row, index) => (
                          <tr key={row.team_id} className="border-t border-white/5">
                            <td className="px-2 py-1.5 tabular-nums text-text-secondary">{index + 1}</td>
                            <td className="max-w-[9rem] truncate px-2 py-1.5 text-white">{row.name}</td>
                            <td className="px-2 py-1.5 text-end font-mono tabular-nums text-white/80">{row.team_id}</td>
                            <td className="px-2 py-1.5 text-end tabular-nums text-white/80">{row.tickets}</td>
                            <td className="px-2 py-1.5 text-end font-mono tabular-nums text-white/80" dir="ltr">{row.key}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

export default FairnessCard
